import { useState } from 'react'
import { HelpCircle, Info as InfoIcon, ChevronDown, ChevronRight } from 'lucide-react'
import { Tooltip } from '../../../../components/ui/Tooltip'
import { useScopeStore } from '../../store'
import { getGuidance } from '../../guidance'
import { getFieldById, getGroupById, getSectionById } from '../../schema'
import { AML_RELATIONSHIPS } from '../../types'
import { SectionHeader } from './shared'

export function AdvancedAmlSection() {
  const { scope, updateAmlConfig } = useScopeStore()
  const { amlConfig } = scope
  const disabled = scope.finalized

  // --- AML fields ---
  const amlRelationshipVendorSection = getSectionById('aml_relationship_vendor')
  const amlRelationshipGroup = getGroupById('aml_relationship_group')
  const amlRelationshipField = getFieldById('aml_relationship')
  const amlFuzzinessGroup = getGroupById('aml_fuzziness_group')
  const amlFuzzinessField = getFieldById('aml_fuzziness')
  const amlPepQueryEnrichmentGroup = getGroupById('aml_pep_query_enrichment_group')
  const amlSendCountriesForPepField = getFieldById('aml_send_countries_for_pep')
  const amlCustomConfigSection = getSectionById('aml_custom_configuration')
  const amlCustomConfigGroup = getGroupById('aml_custom_configuration_group')
  const amlCustomBlockListField = getFieldById('aml_custom_block_list')

  const [isFuzzinessInfoOpen, setIsFuzzinessInfoOpen] = useState(false)

  const FUZZINESS_OPTIONS = [
    { value: 0, label: 'Exact Match (0%)' },
    { value: 10, label: 'Very Strict (10%)' },
    { value: 20, label: 'Strict (20%)' },
    { value: 30, label: 'Recommended (30%)' },
    { value: 40, label: 'Moderate (40%)' },
    { value: 50, label: 'Loose (50%)' },
    { value: 70, label: 'Broad (70%)' },
    { value: 100, label: 'Very Broad (100%)' },
  ]

  const getFuzzinessLabel = (value: number) => {
    return FUZZINESS_OPTIONS.find((o) => o.value === value)?.label ?? `${value}%`
  }

  return (
    <>
      {/* Relationship & vendor */}
      <SectionHeader
        title={amlRelationshipVendorSection?.formTitle || 'Relationship'}
        subtitle={
          amlRelationshipVendorSection?.formDescription ||
          'Vendor relationship model'
        }
      />

      <div className="ml-3 pl-4 border-l-2 border-[var(--border-color)] space-y-4">
      {/* Relationship */}
      <div className="border border-[var(--border-color)] rounded-lg p-4">
        <h4 className="text-sm font-medium text-[var(--text-primary)] mb-1">
          {amlRelationshipGroup?.formTitle || 'AML Relationship'}
        </h4>
        <p className="text-xs text-[var(--text-muted)] mt-1">
          {amlRelationshipGroup?.formDescription ||
            amlRelationshipField?.formDescription ||
            'How will the AML vendor relationship be managed?'}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-[var(--text-secondary)] mb-2">
              {amlRelationshipField?.formLabel || 'Relationship'}
            </label>
            <select
              className="w-full h-12 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
              value={amlConfig.relationship}
              onChange={(e) => updateAmlConfig('relationship', e.target.value as any)}
              disabled={disabled}
            >
              {(
                amlRelationshipField?.options || AML_RELATIONSHIPS.map((r) => ({ value: r.id, label: r.label }))
              ).map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-[var(--text-muted)] mt-1">
              <span className="text-primary">*</span>{' '}
              {amlRelationshipGroup?.helperText || 'Required for completion.'}
            </p>
          </div>
        </div>
      </div>

      {/* Name Matching Fuzziness */}
      <div className="border border-[var(--border-color)] rounded-lg p-4">
        <h4 className="text-sm font-medium text-[var(--text-primary)] mb-1">
          {amlFuzzinessGroup?.formTitle || amlFuzzinessField?.formLabel || 'Name Matching Fuzziness'}
          <Tooltip
            content={
              getGuidance('AML', 'Name matching fuzziness') ||
              amlFuzzinessField?.tooltip ||
              amlFuzzinessGroup?.tooltip ||
              ''
            }
          >
            <span className="ml-2 inline-flex align-middle">
              <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
            </span>
          </Tooltip>
        </h4>
        <p className="text-xs text-[var(--text-muted)] mb-4">
          {amlFuzzinessGroup?.formDescription ||
            amlFuzzinessField?.formDescription ||
            'How strict should name matching be? Lower values = fewer false positives, higher values = fewer missed matches'}
        </p>
        <div className="space-y-3">
          <style>{`
            input[type="range"].fuzziness-slider::-webkit-slider-thumb {
              appearance: none;
              width: 18px;
              height: 18px;
              border-radius: 50%;
              background: rgb(59 130 246);
              cursor: pointer;
              border: 2px solid white;
              box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
            }
            input[type="range"].fuzziness-slider::-moz-range-thumb {
              width: 18px;
              height: 18px;
              border-radius: 50%;
              background: rgb(59 130 246);
              cursor: pointer;
              border: 2px solid white;
              box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
            }
            input[type="range"].fuzziness-slider:disabled::-webkit-slider-thumb {
              background: rgb(100 116 139);
              cursor: not-allowed;
            }
            input[type="range"].fuzziness-slider:disabled::-moz-range-thumb {
              background: rgb(100 116 139);
              cursor: not-allowed;
            }
          `}</style>
          <input
            type="range"
            min="0"
            max="100"
            step="10"
            className="w-full h-2 rounded-lg appearance-none cursor-pointer fuzziness-slider"
            style={{
              background: `linear-gradient(to right, rgb(59 130 246) 0%, rgb(59 130 246) ${amlConfig.fuzziness}%, var(--bg-secondary) ${amlConfig.fuzziness}%, var(--bg-secondary) 100%)`
            }}
            value={amlConfig.fuzziness}
            onChange={(e) => updateAmlConfig('fuzziness', Number(e.target.value))}
            disabled={disabled}
          />
          <div className="flex justify-between text-xs text-[var(--text-muted)]">
            <span>Exact</span>
            <span className="text-primary font-medium">{getFuzzinessLabel(amlConfig.fuzziness)}</span>
            <span>Broad</span>
          </div>
        </div>

        {/* Collapsible drawer toggle button for detailed information */}
        <div className="mt-4">
          <button
            type="button"
            className="w-full flex items-center justify-between gap-3 px-4 py-2 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg hover:bg-[var(--bg-tertiary)] transition-colors"
            onClick={() => setIsFuzzinessInfoOpen(!isFuzzinessInfoOpen)}
          >
            <div className="flex items-center gap-2">
              {isFuzzinessInfoOpen ? (
                <ChevronDown size={16} className="text-[var(--text-secondary)]" />
              ) : (
                <ChevronRight size={16} className="text-[var(--text-secondary)]" />
              )}
              <span className="text-xs font-medium text-[var(--text-primary)]">
                About Fuzziness & How It Works
              </span>
            </div>
            <InfoIcon size={14} className="text-[var(--text-muted)]" />
          </button>
        </div>

        {/* Detailed Fuzziness Information - Collapsible */}
        {isFuzzinessInfoOpen && (
          <div className="mt-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg p-4 space-y-3">
          <div>
            <h5 className="text-xs font-semibold text-[var(--text-primary)] mb-2">What is Fuzziness Level?</h5>
            <p className="text-xs text-[var(--text-secondary)] leading-relaxed">
              Fuzziness (Edit/Levenshtein Distance) is a name matching technique used to reduce the impact of misspellings and small variations in name spelling.
              It accounts for characters that are:
            </p>
            <ul className="mt-2 ml-4 text-xs text-[var(--text-secondary)] space-y-1">
              <li>• <strong>Inserted</strong> (e.g., Yeltsin → Yeltsi<span className="text-primary">i</span>n)</li>
              <li>• <strong>Omitted</strong> (e.g., Yeltsin → Yelts<span className="text-red-400 line-through">i</span>n)</li>
              <li>• <strong>Replaced</strong> (e.g., Yeltsin → Y<span className="text-primary">a</span>ltsin)</li>
            </ul>
          </div>

          <div>
            <h5 className="text-xs font-semibold text-[var(--text-primary)] mb-2">How Does It Work?</h5>
            <p className="text-xs text-[var(--text-secondary)] leading-relaxed">
              To reduce false positives, we cap the maximum edit distance change at <strong>one character</strong>.
              This allows for spelling errors/variations without returning large numbers of unnecessary false positives.
            </p>
          </div>

          <div>
            <h5 className="text-xs font-semibold text-[var(--text-primary)] mb-2">Sensitivity Guide</h5>
            <div className="space-y-2">
              <div className="text-xs">
                <div className="font-medium text-[var(--text-primary)]">Low (0-10%)</div>
                <div className="text-[var(--text-muted)] ml-3">
                  • Christopher <span className="text-red-400">won't match</span> Christophar<br />
                  • Cris <span className="text-red-400">won't match</span> Crih
                </div>
              </div>
              <div className="text-xs">
                <div className="font-medium text-[var(--text-primary)]">Medium (~30-40%)</div>
                <div className="text-[var(--text-muted)] ml-3">
                  • Christopher <span className="text-green-400">matches</span> Christophar<br />
                  • Cris <span className="text-red-400">won't match</span> Crih
                </div>
              </div>
              <div className="text-xs">
                <div className="font-medium text-[var(--text-primary)]">Broad (+70%)</div>
                <div className="text-[var(--text-muted)] ml-3">
                  • Christopher <span className="text-green-400">matches</span> Christophar<br />
                  • Cris <span className="text-green-400">matches</span> Crih
                </div>
              </div>
            </div>
          </div>

          <div>
            <h5 className="text-xs font-semibold text-[var(--text-primary)] mb-2">Minimum Word Length by Fuzziness</h5>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-[var(--border-color)]">
                    <th className="text-left py-1.5 px-2 text-[var(--text-secondary)] font-medium">Setting</th>
                    <th className="text-right py-1.5 px-2 text-[var(--text-secondary)] font-medium">Min Length</th>
                  </tr>
                </thead>
                <tbody className="text-[var(--text-muted)]">
                  <tr className="border-b border-[var(--border-color)]/50">
                    <td className="py-1.5 px-2">Exact Match (0%)</td>
                    <td className="py-1.5 px-2 text-right">No variation</td>
                  </tr>
                  <tr className="border-b border-[var(--border-color)]/50">
                    <td className="py-1.5 px-2">Very Strict (10%)</td>
                    <td className="py-1.5 px-2 text-right">25 chars</td>
                  </tr>
                  <tr className="border-b border-[var(--border-color)]/50">
                    <td className="py-1.5 px-2">Strict (20%)</td>
                    <td className="py-1.5 px-2 text-right">13 chars</td>
                  </tr>
                  <tr className="border-b border-[var(--border-color)]/50">
                    <td className="py-1.5 px-2">Recommended (30%)</td>
                    <td className="py-1.5 px-2 text-right">9 chars</td>
                  </tr>
                  <tr className="border-b border-[var(--border-color)]/50">
                    <td className="py-1.5 px-2">Moderate (40%)</td>
                    <td className="py-1.5 px-2 text-right">7 chars</td>
                  </tr>
                  <tr className="border-b border-[var(--border-color)]/50">
                    <td className="py-1.5 px-2">Loose (50%)</td>
                    <td className="py-1.5 px-2 text-right">5 chars</td>
                  </tr>
                  <tr className="border-b border-[var(--border-color)]/50">
                    <td className="py-1.5 px-2">Broad (70%)</td>
                    <td className="py-1.5 px-2 text-right">4 chars</td>
                  </tr>
                  <tr className="border-b border-[var(--border-color)]/50">
                    <td className="py-1.5 px-2">Very Broad (80-90%)</td>
                    <td className="py-1.5 px-2 text-right">3 chars</td>
                  </tr>
                  <tr>
                    <td className="py-1.5 px-2">Maximum (100%)</td>
                    <td className="py-1.5 px-2 text-right">3 chars</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-xs text-[var(--text-muted)] mt-2 italic">
              Words shorter than the minimum length will use exact matching regardless of the fuzziness setting.
            </p>
          </div>
        </div>
        )}
      </div>

      {/* PEP screening query enrichment */}
      <div className="border border-[var(--border-color)] rounded-lg p-4">
        <h4 className="text-sm font-medium text-[var(--text-primary)] mb-1">
          {amlPepQueryEnrichmentGroup?.formTitle ||
            amlSendCountriesForPepField?.formLabel ||
            'PEP screening query enrichment'}
          <Tooltip
            content={
              getGuidance('AML', 'PEP screening query enrichment') ||
              amlSendCountriesForPepField?.tooltip ||
              amlPepQueryEnrichmentGroup?.tooltip ||
              ''
            }
          >
            <span className="ml-2 inline-flex align-middle">
              <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
            </span>
          </Tooltip>
        </h4>
        <p className="text-xs text-[var(--text-muted)] mt-1">
          {amlPepQueryEnrichmentGroup?.formDescription ||
            amlSendCountriesForPepField?.formDescription ||
            'Whether to include country information in the screening search query (accuracy vs. comprehensiveness trade-off).'}
        </p>
        <div className="mt-3">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              className="w-4 h-4 rounded border-[var(--border-color)] bg-[var(--bg-secondary)] text-primary focus:ring-primary"
              checked={amlConfig.sendCountriesForPep}
              onChange={(e) => updateAmlConfig('sendCountriesForPep', e.target.checked)}
              disabled={disabled}
            />
            <span className="text-sm text-[var(--text-primary)]">
              {amlSendCountriesForPepField?.formLabel ||
                'Include countries of citizenship/residence for PEP screening'}
            </span>
          </label>
        </div>
      </div>

      {/* Batch Processing */}
      <div className="border border-[var(--border-color)] rounded-lg p-4">
        <h4 className="text-sm font-medium text-[var(--text-primary)] mb-1">
          Batch Processing
        </h4>
        <p className="text-xs text-[var(--text-muted)] mt-1">
          Does the customer require batch processing capability for AML screening?
        </p>
        <div className="mt-3">
          <select
            className="w-full h-12 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
            value={amlConfig.batchProcessing?.required || 'unknown'}
            onChange={(e) =>
              updateAmlConfig('batchProcessing', {
                ...amlConfig.batchProcessing,
                required: e.target.value as any,
              })
            }
            disabled={disabled}
          >
            <option value="unknown">Don't know</option>
            <option value="not_required">Not required</option>
            <option value="required_regularly">Required regularly</option>
            <option value="required_once">Required once</option>
          </select>
        </div>

        {amlConfig.batchProcessing?.required &&
          amlConfig.batchProcessing.required !== 'not_required' &&
          amlConfig.batchProcessing.required !== 'unknown' && (
            <div className="mt-3">
              <label className="block text-sm text-[var(--text-secondary)] mb-2">
                Additional Details
              </label>
              <textarea
                className="w-full h-20 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50 resize-none"
                placeholder="e.g., Expected volume, frequency, file format preferences..."
                value={amlConfig.batchProcessing?.details || ''}
                onChange={(e) =>
                  updateAmlConfig('batchProcessing', {
                    ...amlConfig.batchProcessing,
                    details: e.target.value,
                  })
                }
                disabled={disabled}
              />
            </div>
          )}
      </div>
      </div>

      {/* Custom configuration */}
      <SectionHeader
        title={amlCustomConfigSection?.formTitle || 'Custom configuration'}
        subtitle={
          amlCustomConfigSection?.formDescription ||
          'Customer-specific lists beyond standard coverage'
        }
      />

      <div className="ml-3 pl-4 border-l-2 border-[var(--border-color)] space-y-4">
      <div className="border border-[var(--border-color)] rounded-lg p-4">
        <h4 className="text-sm font-medium text-[var(--text-primary)] mb-3">
          {amlCustomConfigGroup?.formTitle || amlCustomConfigSection?.formTitle || 'Custom Block List (Optional)'}
        </h4>
        <div>
          <label className="block text-sm text-[var(--text-secondary)] mb-2">
            {amlCustomBlockListField?.formLabel || 'Custom Block List'}
          </label>
          <p className="text-xs text-[var(--text-muted)] mt-1 mb-2">
            {amlCustomBlockListField?.formDescription ||
              'Specify any custom sanctions lists or watchlists the customer requires beyond standard coverage.'}
          </p>
          <textarea
            className="w-full h-24 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50 resize-none"
            placeholder={
              amlCustomBlockListField?.placeholder ||
              'Enter custom sanctions lists, watchlists, or other blocking criteria...'
            }
            value={amlConfig.customBlockList}
            onChange={(e) => updateAmlConfig('customBlockList', e.target.value)}
            disabled={disabled}
          />
        </div>
      </div>

      {/* AML Data Aging */}
      <div className="space-y-4 mt-6">
        <div className="pt-2">
          <h4 className="text-base font-semibold text-[var(--text-primary)]">AML Data Aging</h4>
        </div>

        <div className="flex items-start gap-3 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
          <InfoIcon size={16} className="text-blue-400 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-[var(--text-primary)]">
            <p className="mb-2">
              AML aging determines how long screening results remain valid before requiring re-screening.
              The aging system evaluates process results based on their creation dates and discards those
              exceeding the configured age threshold.
            </p>
            <p className="font-medium">Default: 12 months (1 year)</p>
          </div>
        </div>

        <div className="space-y-4">
          <label className="flex items-center gap-2 text-sm font-medium text-[var(--text-primary)] cursor-pointer">
            <input
              type="checkbox"
              checked={amlConfig.agingEnabled}
              onChange={(e) => updateAmlConfig('agingEnabled', e.target.checked)}
              disabled={disabled}
              className="rounded border-[var(--border-color)] bg-[var(--bg-primary)] text-blue-500 focus:ring-blue-500"
            />
            Use Custom Aging Period
          </label>

          {amlConfig.agingEnabled && (
            <>
              <div>
                <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                  Aging Period (Months)
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    min="1"
                    max="60"
                    value={amlConfig.agingPeriodMonths}
                    onChange={(e) => updateAmlConfig('agingPeriodMonths', parseInt(e.target.value) || 12)}
                    disabled={disabled}
                    className="w-32 px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
                  />
                  <span className="text-sm text-[var(--text-secondary)]">months</span>
                  <span className="text-xs text-[var(--text-muted)]">
                    ({Math.round((amlConfig.agingPeriodMonths / 12) * 10) / 10}{' '}
                    {amlConfig.agingPeriodMonths === 12 ? 'year' : 'years'})
                  </span>
                </div>
                <p className="text-xs text-[var(--text-muted)] mt-2">
                  AML screening results older than {amlConfig.agingPeriodMonths} months will require re-screening
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                  Additional Aging Configuration (Optional)
                </label>
                <textarea
                  value={amlConfig.agingCustomConfig}
                  onChange={(e) => updateAmlConfig('agingCustomConfig', e.target.value)}
                  disabled={disabled}
                  placeholder="Enter any custom aging rules or specific requirements for different check types (e.g., PEP, Sanctions, Adverse Media)"
                  rows={3}
                  className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 text-sm"
                />
                <p className="text-xs text-[var(--text-muted)] mt-1">
                  Specify if you need different aging periods for specific check types or special requirements
                </p>
              </div>
            </>
          )}
        </div>
      </div>
      </div>
    </>
  )
}
